import { useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Building, Plug, FolderCog, ShieldAlert, ScrollText, FileText, Shield } from 'lucide-react';
import { CompanySection } from '../components/settings/CompanySection';
import { AppearanceSection } from '../components/settings/AppearanceSection';
import { PasswordSection } from '../components/settings/PasswordSection';
import { IntegrationsSection } from '../components/settings/IntegrationsSection';
import { AccountLimitsSection } from '../components/settings/AccountLimitsSection';
import { PolicyEditor } from '../components/policy/PolicyEditor';
import { WhatsAppGroupsAdmin } from '../components/WhatsAppGroupsAdmin';
import { CrmSection } from '../components/CrmSection';
import { DataHygieneSection } from '../components/DataHygieneSection';
import { DocumentsSection } from '../components/DocumentsSection';

const TABS = [
  { id: 'general', label: 'General', icon: Building, roles: ['owner', 'admin', 'user'] },
  { id: 'integrations', label: 'Integrations', icon: Plug, roles: ['owner'] },
  { id: 'crm', label: 'CRM', icon: FolderCog, roles: ['owner'] },
  { id: 'documents', label: 'Documents', icon: FileText, roles: ['owner', 'admin'] },
  { id: 'policy', label: 'Politique', icon: ScrollText, roles: ['owner'] },
  { id: 'maintenance', label: 'Maintenance', icon: ShieldAlert, roles: ['owner', 'admin'] },
];

export default function SettingsPage() {
  const { token, tenant, user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');

  const isOwner = user?.role === 'owner';
  const userRole = user?.role || 'user';
  const visibleTabs = TABS.filter((t) => t.roles.includes(userRole));

  return (
    <DashboardLayout title="Paramètres">
      <div className="max-w-3xl space-y-6">
        {/* Tab navigation */}
        <nav
          className="flex gap-1 border-b overflow-x-auto pb-px"
          data-testid="settings-tab-nav"
        >
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                }`}
                data-testid={`settings-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Tab content */}
        <div className="space-y-6">
          {activeTab === 'general' && (
            <>
              <CompanySection token={token} tenant={tenant} isOwner={isOwner} />
              <AppearanceSection />
              <PasswordSection token={token} />
              <AccountLimitsSection />
            </>
          )}

          {activeTab === 'integrations' && isOwner && (
            <IntegrationsSection token={token} />
          )}

          {activeTab === 'crm' && isOwner && (
            <CrmSection token={token} />
          )}

          {activeTab === 'documents' && (isOwner || userRole === 'admin') && (
            <DocumentsSection token={token} user={user} />
          )}

          {activeTab === 'policy' && isOwner && (
            <div className="space-y-6">
              {/* Explanation banner */}
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4" data-testid="policy-info-banner">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">Comment fonctionne la politique des clones ?</h3>
                <p className="text-xs text-blue-800 leading-relaxed">
                  Vos clones suivent <strong>deux niveaux de règles qui se cumulent</strong> :<br />
                  1. <strong>Règles de la plateforme</strong> (socle commun) — définies par l'administrateur, elles s'appliquent à tous les tenants. Affichées ci-dessous en lecture seule.<br />
                  2. <strong>Règles de votre entreprise</strong> (personnalisées) — vos ajouts spécifiques, qui viennent <strong>en complément</strong> des règles plateforme. Elles ne les remplacent pas.
                </p>
              </div>

              {/* Global policy (read-only) */}
              <PolicyEditor
                title="Règles Plateforme"
                description="Ces règles sont définies au niveau de la plateforme et s'appliquent à tous les clones. Elles sont modifiables uniquement par l'administrateur."
                icon={Shield}
                fetchUrl="/tenants/global-policy-preview"
                addUrl=""
                updateUrl=""
                deleteUrl=""
                token={token}
                readOnly
              />

              {/* Company policy (editable) */}
              <PolicyEditor
                title="Politique Entreprise"
                description="Ajoutez ici vos règles spécifiques. Elles s'appliquent à tous les clones de votre entreprise, en complément des règles plateforme."
                icon={Building}
                fetchUrl="/tenants/company-policy"
                addUrl="/tenants/company-policy/rules"
                updateUrl="/tenants/company-policy/rules"
                deleteUrl="/tenants/company-policy/rules"
                token={token}
              />
            </div>
          )}

          {activeTab === 'maintenance' && (isOwner || userRole === 'admin') && (
            <>
              <DataHygieneSection token={token} />
              {isOwner && <WhatsAppGroupsAdmin token={token} />}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
